import React from "react";
import { styled, Box } from "@mui/material";
import { ValueOf } from "../../../../../src/domains/shared/constants/constants";
import {
  PROJECT_CONDITION,
  is2dCompletedTimeProject,
  is3dCompletedTimeProject,
} from "../../../../../src/domains/shared/constants/constants";
import { COLOR_CONSTANTS } from "src/domains/shared/constants/styles";
import useLocale from "src/domains/shared/hooks/localeHook/useLocale";
import { useTranslation } from "react-i18next";

// status → i18n 키 매핑. styled callback / utility 함수에서 hook 호출 불가하므로
// 키만 반환하고 컴포넌트 body 에서 t(key) 호출한다.
const STATUS_LABEL_KEYS = {
  before: "status.before",
  requested: "status.requested",
  on_processing: "status.onProcessing",
  failed: "status.failed",
  reviewing: "status.reviewing",
  completed: "status.completed",
  review_2d_ready: "status.completed2d",
  review_3d_ready: "status.completed3d",
} as const;

/** 모르는 상태 — 처리 전과 같은 회색이다. */
const UNKNOWN_STATUS_STYLES = {
  backgroundColor: "transparent",
  color: "rgba(0, 0, 0, 0.38)",
  indicatorColor: "rgba(0, 0, 0, 0.12)",
  labelKey: STATUS_LABEL_KEYS.before,
};

/**
 * 프로젝트 처리 상태 칩 props — `PROJECT_CONDITION` 6 상태(`before`/`requested`/`on_processing`/`failed`/`reviewing`/`completed`)와,
 * 검수 모드인 탭에서만 「2D 완료」·「3D 완료」로 서는 `review_2d_ready`·`review_3d_ready` 에 대응한다.
 *
 * 내부에서 `useLocale()` + `react-i18next` 로 다국어 라벨을 자동 매핑한다.
 */
export interface StatusChipProps {
  /**
   * 상태 타입
   */
  status: ValueOf<typeof PROJECT_CONDITION>;
  /**
   * 표시할 텍스트 (기본값은 상태에 따른 한국어 텍스트)
   */
  label?: string;
  /**
   * 추가 CSS 클래스명
   */
  className?: string;
  /**
   * 라벨 표시 여부
   */
  showLabel?: boolean;
  /**
   * 칩 크기 — `small` 은 좁은 푸터에서 여러 상태를 나열할 때 쓴다.
   */
  size?: "small" | "medium";
  /** 테스트 앵커. 루트로 전파된다(`...props`). 값 규약은 `src/test/testIds.ts` 참조. */
  "data-testid"?: string;
}

// Status 별 색상만 반환 (label 은 컴포넌트 body 에서 t 호출). styled callback /
// utility 함수에서 hook 호출이 불가능하므로 분리.
const getStatusStyles = (status: ValueOf<typeof PROJECT_CONDITION>) => {
  switch (status) {
    case "before":
      return {
        backgroundColor: "transparent",
        color: "rgba(0, 0, 0, 0.38)",
        indicatorColor: "rgba(0, 0, 0, 0.12)",
        labelKey: STATUS_LABEL_KEYS.before,
      };
    case "requested":
      return {
        backgroundColor: "transparent",
        color: "rgba(0, 0, 0, 0.38)",
        indicatorColor: "rgba(0, 0, 0, 0.38)",
        labelKey: STATUS_LABEL_KEYS.requested,
      };
    case "on_processing":
      return {
        backgroundColor: "transparent",
        color: "#03a9f4",
        indicatorColor: "#03a9f4",
        labelKey: STATUS_LABEL_KEYS.on_processing,
      };
    case "failed":
      return {
        backgroundColor: "transparent",
        color: "#ef5350",
        indicatorColor: "#ef5350",
        labelKey: STATUS_LABEL_KEYS.failed,
      };
    case "reviewing":
      return {
        backgroundColor: "transparent",
        color: "#ff9800",
        indicatorColor: "#ff9800",
        labelKey: STATUS_LABEL_KEYS.reviewing,
      };
    case "completed":
      return {
        backgroundColor: "transparent",
        color: "#4caf50",
        indicatorColor: "#4caf50",
        labelKey: STATUS_LABEL_KEYS.completed,
      };
    case "review_2d_ready":
      // 검수 모드의 값이다 — 보통 탭에 오면(서버의 결함) 2D 완료 술어가 닫고, 모르는 상태로 보인다.
      return is2dCompletedTimeProject({ current_state: status })
        ? {
            backgroundColor: "transparent",
            color: COLOR_CONSTANTS.COLOR.TEAL[500],
            indicatorColor: COLOR_CONSTANTS.COLOR.TEAL[500],
            labelKey: STATUS_LABEL_KEYS.review_2d_ready,
          }
        : UNKNOWN_STATUS_STYLES;
    case "review_3d_ready":
      // 검수 모드의 값이다 — 보통 탭에 오면(서버의 결함) 3D 완료 술어가 닫고, 모르는 상태로 보인다.
      return is3dCompletedTimeProject({ current_state: status })
        ? {
            backgroundColor: "transparent",
            color: COLOR_CONSTANTS.COLOR.INDIGO[500],
            indicatorColor: COLOR_CONSTANTS.COLOR.INDIGO[500],
            labelKey: STATUS_LABEL_KEYS.review_3d_ready,
          }
        : UNKNOWN_STATUS_STYLES;
    default:
      return UNKNOWN_STATUS_STYLES;
  }
};

const SIZE_STYLES = {
  medium: {
    gap: "8px",
    padding: "4px 8px",
    fontSize: "12px",
    lineHeight: "18px",
    minHeight: "22px",
    indicator: "8px",
  },
  small: {
    gap: "6px",
    padding: "2px 6px",
    fontSize: "11px",
    lineHeight: "16px",
    minHeight: "18px",
    indicator: "6px",
  },
} as const;

// Styled Container
const StyledContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "status" && prop !== "size",
})<{
  status: ValueOf<typeof PROJECT_CONDITION>;
  size: "small" | "medium";
}>(({ status, size }) => {
  const styles = getStatusStyles(status);
  const sizing = SIZE_STYLES[size];

  return {
    display: "inline-flex",
    alignItems: "center",
    gap: sizing.gap,
    padding: sizing.padding,
    borderRadius: "1000px", // 완전한 둥근 모서리
    backgroundColor: styles.backgroundColor,
    color: styles.color,
    fontFamily: "Pretendard",
    fontWeight: 500,
    fontSize: sizing.fontSize,
    lineHeight: sizing.lineHeight,
    letterSpacing: "0.16px",
    minHeight: sizing.minHeight,
    whiteSpace: "nowrap",
    transition: "all 0.2s ease-in-out",

    // 호버 상태
    "&:hover": {
      opacity: 0.8,
    },

    // 포커스 상태
    "&:focus-visible": {
      outline: `2px solid ${styles.color}`,
      outlineOffset: "2px",
    },
  };
});

// Styled Indicator (왼쪽 원형 인디케이터)
const StyledIndicator = styled("div", {
  shouldForwardProp: (prop) => prop !== "status" && prop !== "size",
})<{
  status: ValueOf<typeof PROJECT_CONDITION>;
  size: "small" | "medium";
}>(({ status, size }) => {
  const styles = getStatusStyles(status);

  return {
    width: SIZE_STYLES[size].indicator,
    height: SIZE_STYLES[size].indicator,
    borderRadius: "50%",
    backgroundColor: styles.indicatorColor,
    flexShrink: 0,
  };
});

/**
 * StatusChip 컴포넌트
 *
 * 6가지 상태를 나타내는 칩 컴포넌트입니다.
 * 각 상태별로 고유한 색상과 스타일을 가집니다.
 * Figma 디자인 시스템을 기반으로 구현되었습니다.
 */
export const StatusChip: React.FC<StatusChipProps> = ({
  status,
  label,
  className,
  showLabel = true,
  size = "medium",
  ...props
}) => {
  const { t } = useTranslation("projects");
  const styles = getStatusStyles(status);
  const displayLabel = label || t(styles.labelKey, { ns: "projects" });

  return (
    <StyledContainer
      status={status}
      size={size}
      className={className}
      {...props}
    >
      <StyledIndicator status={status} size={size} />
      {showLabel && displayLabel}
    </StyledContainer>
  );
};

export default StatusChip;
